
import React, { useState, useMemo } from 'react';
import { useGuitarMatch } from '../../context/GuitarMatchContext';
import { Recorder } from '../studio/Recorder';
import { RecordingResult, AppState, RecordingMetadata, MatchType } from '../../types';

interface SkillAssessmentProps {
    onComplete: () => void;
    onCancel: () => void;
    startAnalysisOverride: (file: File, metadata?: RecordingMetadata, qualityWarning?: string, matchTypeOverride?: MatchType) => Promise<void>;
}

export const SkillAssessment: React.FC<SkillAssessmentProps> = ({ onComplete, onCancel, startAnalysisOverride }) => {
    const { lang, setMatchType, setManualGrid, setTargetBpm, timeSignature, setCoachLevel, setShowTutorial } = useGuitarMatch();
    const [step, setStep] = useState<'INTRO' | 'TEST'>('INTRO');

    const grid = ["G", "C", "|", "D", "|", "G"];
    const bpm = 90;

    const maxBeats = useMemo(() => {
        const beatsPerBar = parseInt(timeSignature.split('/')[0]) || 4;
        const measureCount = grid.filter(t => t === '|').length + 1;
        return (measureCount * beatsPerBar);
    }, [timeSignature]);

    const handleRecordingComplete = async (res: RecordingResult) => {
        onComplete(); // Ferme l'overlay immédiatement
        
        // On délègue l'analyse au parent stable via startAnalysisOverride
        setTimeout(() => {
            localStorage.setItem('gsc_is_assessment', 'true');
            if (res.metadata) {
                res.metadata.source = 'SKILL_ASSESSMENT';
                res.metadata.recordedAtBpm = bpm;
            }
            setMatchType('ASSESSMENT');
            setManualGrid("G C | D | G");
            setTargetBpm(bpm);
            
            startAnalysisOverride(res.file, res.metadata, undefined, 'ASSESSMENT');
        }, 100);
    };

    const handleTooComplated = () => {
        // Bypass vers le niveau 1 et lancement du tuto
        setCoachLevel(1);
        localStorage.setItem('gsc_level_calibrated', 'true');
        setShowTutorial(true);
        onCancel();
    };

    if (step === 'INTRO') {
        return (
            <div className="fixed inset-0 z-[10500] flex items-center justify-center bg-black/95 backdrop-blur-3xl p-6 animate-fade-in">
                <div className="max-w-2xl w-full bg-[#02080a] border border-white/10 p-10 md:p-14 rounded-[64px] shadow-[0_0_100px_rgba(0,0,0,0.8)] text-center space-y-10 relative overflow-hidden">
                    {/* Background decoration */}
                    <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-cyan-500 via-white to-cyan-500"></div>
                    
                    <div className="w-24 h-24 rounded-full bg-cyan-500/10 border border-cyan-500/30 flex items-center justify-center mx-auto text-5xl shadow-[0_0_40px_rgba(6,182,212,0.3)] animate-pulse-slow">
                        🎛️
                    </div>
                    
                    <div className="space-y-4">
                        <h2 className="text-4xl md:text-5xl font-black text-white italic uppercase tracking-tighter leading-none">
                            Calibration <br/><span className="text-cyan-400">Automatique</span>
                        </h2>
                        
                        <div className="space-y-4 px-4">
                            <p className="text-slate-300 text-sm font-medium leading-relaxed max-w-md mx-auto">
                                "Pour que le Coach soit efficace, il doit s'adapter à votre niveau actuel. Ni trop dur, ni trop facile."
                            </p>
                        </div>
                    </div>

                    <div className="bg-white/[0.03] p-8 rounded-[40px] border border-white/5 space-y-6 text-left relative">
                        <div className="flex items-center gap-4 mb-2">
                            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center font-black text-white">1</div>
                            <p className="text-xs text-slate-300"><span className="text-white font-bold">Enregistrez</span> la séquence d'accords ci-dessous.</p>
                        </div>
                        <div className="flex justify-center gap-4 py-2">
                            {["G", "C", "D", "G"].map((chord, i) => (
                                <div key={i} className="w-14 h-14 rounded-2xl bg-black border border-white/10 flex items-center justify-center text-xl font-black italic text-white shadow-lg">
                                    {chord}
                                </div>
                            ))}
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center font-black text-white">2</div>
                            <p className="text-xs text-slate-300">L'IA analyse votre <span className="text-cyan-400 font-bold">précision</span> et règle la sévérité du coach.</p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-6 pt-4">
                        <button 
                            onClick={() => setStep('TEST')}
                            className="w-full py-6 bg-white text-black rounded-full font-black uppercase text-xs tracking-[0.3em] shadow-2xl hover:scale-105 transition-all"
                        >
                            LANCER LE TEST ⚡
                        </button>
                        
                        <button 
                            onClick={handleTooComplated}
                            className="text-[10px] font-bold text-slate-500 uppercase tracking-widest hover:text-white transition-colors"
                        >
                            Passer et démarrer au Niveau 1
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-[10500] bg-black">
            <Recorder 
                mode="video" 
                onRecordingComplete={handleRecordingComplete} 
                onCancel={onCancel}
                progression={grid}
                bpm={bpm}
                lang={lang}
                autoStopAtBeat={maxBeats}
                forceStudentView={true} // AXE 2 : Force la vue élève
            />
        </div>
    );
};
